<template>
    <div>
        <nav class="flex items-center justify-center" aria-label="Progress">
            <p class="text-sm font-medium">
                {{ $t('messages.step_X of_Y', { X: step, Y: 3 }) }}
            </p>
            <ol class="ml-8 flex items-center space-x-5 hidden">
                <li>
                    <!-- Completed Step -->
                    <button type="button" class="block w-2.5 h-2.5 bg-indigo-600 rounded-full hover:bg-indigo-900">
                        <span class="sr-only">Step 1</span>
                    </button>
                </li>

                <li>
                    <!-- Current Step -->
                    <a href="#" class="relative flex items-center justify-center" aria-current="step">
                        <span class="absolute w-5 h-5 p-px flex" aria-hidden="true">
                            <span class="w-full h-full rounded-full bg-indigo-200"></span>
                        </span>
                        <span class="relative block w-2.5 h-2.5 bg-indigo-600 rounded-full" aria-hidden="true"></span>
                        <span class="sr-only">Step 2</span>
                    </a>
                </li>

                <li>
                    <!-- Upcoming Step -->
                    <a href="#" class="block w-2.5 h-2.5 bg-gray-200 rounded-full hover:bg-gray-400">
                        <span class="sr-only">Step 3</span>
                    </a>
                </li>
            </ol>
        </nav>

        <div class="pt-2 pb-6 md:py-6" v-show="step === 1">
            <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
                <div class="py-5">
                    <div class="md:grid md:grid-cols-4 md:gap-6">
                        <div class="sm:col-span-2">
                            <label class="block text-sm font-medium leading-5 text-gray-700">
                                {{ $t('messages.number_of_teams') }}
                            </label>
                            <div class="mt-1 rounded-md shadow-sm">
                                <select
                                    id="type"
                                    name="type_id"
                                    v-model="numberOfGroups"
                                    required
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value="" selected disabled>
                                        {{ $t('messages.select') }}
                                    </option>
                                    <option v-for="number in numberOfGroupsOptions" :value="number">
                                        {{ number }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div
                            class="sm:col-span-2"
                            v-show="numberOfGroups <= maxNumberOfGroups && selectedStudents.length > 4"
                        >
                            <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                            <ButtonPrimary @click="step = 2" element="button" type="button" class="mt-1">
                                {{ $t('messages.continue') }}
                            </ButtonPrimary>
                        </div>
                    </div>
                </div>
                <div class="md:grid md:grid-cols-3 md:gap-6">
                    <div class="mt-5 md:mt-0 md:col-span-2">
                        <div class="shadow sm:rounded-md sm:overflow-hidden">
                            <div class="px-4 py-5 bg-white sm:p-6">
                                <div class="mt-3">
                                    <AppButton @click="selectAll()" element="button" type="button">
                                        {{ $t('messages.select_all') }}
                                    </AppButton>

                                    <AppButton @click="deselectAll()" element="button" type="button">
                                        {{ $t('messages.deselect_all') }}
                                    </AppButton>
                                    <AppButton @click="selectPresent()" element="button" type="button">
                                        {{ $t('messages.select_present_students') }}
                                    </AppButton>
                                    <div class="inline-block">({{ selected.length }})</div>
                                </div>

                                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 md:grid-cols-2">
                                    <div
                                        v-for="student in selectedStudents"
                                        @click="remove(student.id)"
                                        class="relative col-span-1 flex shadow rounded-md"
                                    >
                                        <div
                                            class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                                        >
                                            <img :src="student.avatar_or_default" alt="profile photo" />
                                        </div>
                                        <div
                                            class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                                        >
                                            <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                                <div
                                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                                >
                                                    {{ student.full_name }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr class="mt-6" />

                                <div
                                    class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 md:grid-cols-2 opacity-50"
                                >
                                    <div
                                        v-for="student in notSelectedStudents"
                                        @click="add(student.id)"
                                        class="relative col-span-1 flex shadow rounded-md"
                                    >
                                        <div
                                            class="shrink-0 flex items-center justify-center w-16 text-white text-sm leading-5 font-medium rounded-l-md overflow-hidden"
                                        >
                                            <img :src="student.avatar_or_default" alt="profile photo" />
                                        </div>
                                        <div
                                            class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate"
                                        >
                                            <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
                                                <div
                                                    class="text-gray-900 font-medium hover:text-gray-600 transition ease-in-out duration-150"
                                                >
                                                    {{ student.full_name }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="md:col-span-1">
                        <div class="bg-white overflow-hidden sm:rounded-lg sm:shadow">
                            <div class="p-4">
                                <div class="block" v-for="student in students">
                                    <label>
                                        <input
                                            type="checkbox"
                                            :value="student.id"
                                            name="students[]"
                                            v-model="selected"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        {{ student.full_name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="pt-2 pb-6 md:py-6" v-show="step === 2">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div>
                            {{ $tc('messages.step_X', 2, { number: 2 }) }}:
                            {{ $t('messages.team_maker_step_2_explanation') }}
                        </div>
                        <div :class="debug ? 'bg-red-50' : 'bg-gray-100'">
                            <div class="md:grid md:grid-cols-3 md:gap-6 py-6 px-4">
                                <div
                                    v-for="(exclusionGroup, index) in exclusionGroupsStudents"
                                    class="py-2 px-5 bg-white relative"
                                >
                                    <!--                                    <div class="absolute top-0 right-0 h-6 w-6 bg-blue-500">-->
                                    <svg
                                        class="absolute top-0 right-0 w-4 h-4 mt-1 mr-1 text-gray-400 cursor-pointer"
                                        @click="removeExclusionGroup(index)"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                    <!--                                    </div>-->
                                    <ul>
                                        <li v-for="student in exclusionGroup">
                                            <div class="flex shrink-0 space-x-2">
                                                <img
                                                    class="max-w-none h-6 w-6 rounded-full ring-2 ring-white"
                                                    :src="student.avatar_or_default"
                                                    :alt="student.full_name"
                                                />
                                                <div>
                                                    {{ student.name_or_nickname }}
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="md:col-span-1">
                    <div class="bg-white overflow-hidden sm:rounded-lg sm:shadow">
                        <div class="p-4">
                            <div class="flex space-x-2">
                                <div class="sm:col-span-2">
                                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                                    <ButtonPrimary
                                        element="button"
                                        class="mt-1"
                                        @click="addExclusionGroup()"
                                        type="button"
                                        :disabled="exclusionGroupSelector.length < 2"
                                    >
                                        <span v-if="exclusionGroupSelector.length < 2">
                                            {{ $tc('messages.minimum_X_students', 2, { number: 2 }) }}
                                        </span>
                                        <span v-else>
                                            {{ $t('messages.add_exclusion_group') }}
                                            &nbsp; ({{ exclusionGroupSelector.length }})
                                        </span>
                                    </ButtonPrimary>
                                </div>

                                <div class="sm:col-span-2">
                                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                                    <ButtonPrimary element="button" type="button" class="mt-1" @click="createGroups()">
                                        {{ $t('messages.create_teams') }}
                                    </ButtonPrimary>
                                </div>
                            </div>

                            <div class="block" v-for="student in selectedStudents">
                                <label>
                                    <input
                                        type="checkbox"
                                        :value="student.id"
                                        v-model="exclusionGroupSelector"
                                        :disabled="
                                            exclusionGroupSelector.length >= numberOfGroups &&
                                            !exclusionGroupSelector.includes(student.id)
                                        "
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    {{ student.full_name }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="pt-2 pb-6 md:py-6" v-show="step === 3">
            <div class="md:grid md:grid-cols-3 md:gap-6">
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="flex p-5">
                            <div class="rounded-md bg-yellow-50 p-4 w-full" v-if="error !== ''">
                                <div class="flex">
                                    <div class="shrink-0">
                                        <svg
                                            class="h-5 w-5 text-yellow-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                clip-rule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div class="ml-3 flex-1 md:flex md:justify-between">
                                        <p class="text-sm text-yellow-700">
                                            {{ error }}
                                        </p>
                                        <p class="mt-3 text-sm md:mt-0 md:ml-6">
                                            <ButtonPrimary element="button" type="button" @click="goToSecondStep()">
                                                {{ $tc('messages.go_to_step_X', 2, { number: 2 }) }}
                                            </ButtonPrimary>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div v-for="group in groupsStudents" class="pt-2 pl-5 list-disc">
                                <div :class="{ debug: 'bg-green-100' }">
                                    <ul v-for="student in group">
                                        <li class="flex shrink-0 space-x-2">
                                            <img
                                                class="max-w-none h-6 w-6 rounded-full ring-2 ring-white"
                                                :src="student.avatar_or_default"
                                                :alt="student.full_name"
                                            />
                                            <div>
                                                {{ student.name_or_nickname }}
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            &nbsp;
                        </div>

                        <hr />

                        <div :class="debug ? 'bg-red-50' : 'bg-gray-100'" v-show="debug">
                            <div class="md:grid md:grid-cols-3 md:gap-6 py-6 px-4">
                                <div v-for="exclusionGroup in exclusionGroupsStudents" class="py-2 px-5 bg-white">
                                    <ul>
                                        <li v-for="student in exclusionGroup">
                                            <div class="flex shrink-0 space-x-2">
                                                <img
                                                    class="max-w-none h-6 w-6 rounded-full ring-2 ring-white"
                                                    :src="student.avatar_or_default"
                                                    :alt="student.full_name"
                                                />
                                                <div>
                                                    {{ student.name_or_nickname }}
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-5 md:mt-0 md:col-span-1">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="flex p-5">
                            <ButtonPrimary element="button" type="button" @click="goToSecondStep()">
                                {{
                                    $tc('messages.go_to_step_X', 2, {
                                        number: 2,
                                    })
                                }}
                            </ButtonPrimary>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import AppButton from '@/components/AppButton.vue';
import ButtonPrimary from '../ButtonPrimary.vue';

export default {
    components: { ButtonPrimary, AppButton },
    data() {
        return {
            debug: false,
            step: 1,
            numberOfGroups: 2,
            selected: [],
            exclusionGroupSelector: [],
            exclusionGroups: [],
            groups: [], //Teams
            error: '',
        };
    },
    computed: {
        allSelected: function () {
            return this.selected.length === this.students.length;
        },
        noneSelected: function () {
            return this.selected.length === 0;
        },
        selectedStudents: function () {
            return this.students.filter((s) => this.selected.includes(s.id));
        },
        notSelectedStudents: function () {
            return this.students.filter((s) => !this.selected.includes(s.id));
        },
        exclusionGroupsStudents: function () {
            return this.exclusionGroups.map((g) => g.map((s) => this.students.find((student) => student.id === s)));
        },
        groupsStudents: function () {
            return this.groups.map((g) => g.map((s) => this.students.find((student) => student.id === s)));
        },
        maxNumberOfGroups: function () {
            return Math.floor(this.selectedStudents.length / 2); //At most it would divide the class in pairs
        },
        numberOfGroupsOptions: function () {
            const max = this.maxNumberOfGroups;
            const array = [];
            for (let i = 2; i <= max; i++) {
                array.push(i);
            }
            return array;
            //return _.range(2, max); //if we load lodash
        },
    },
    methods: {
        deselectAll() {
            return (this.selected = []);
        },
        selectPresent() {
            this.selected = this.students.filter((s) => !this.absent.includes(s.id)).map((s) => s.id);
        },
        selectAll() {
            return (this.selected = this.students.map((x) => x.id));
        },
        add(id) {
            this.selected.push(id);
        },
        remove(id) {
            const index = this.selected.indexOf(id);
            this.selected.splice(index, 1);
        },
        addExclusionGroup() {
            this.exclusionGroups.push(this.exclusionGroupSelector);
            this.exclusionGroupSelector = [];
        },
        removeExclusionGroup(index) {
            this.exclusionGroups.splice(index, 1);
        },
        goToSecondStep() {
            this.groups = [];
            this.error = '';
            this.step = 2;
        },
        createGroups() {
            let userIds = this.selectedStudents.map((u) => u.id);

            let groups = this.getGroups(userIds, this.numberOfGroups, this.exclusionGroups);

            if (groups === undefined) {
                this.error = this.$t('messages.could_not_create_teams');
            } else {
                this.groups = groups;
            }
            this.step = 3;
        },
        getGroups(userIds, numberOfGroups, exclusionGroups) {
            function groupsThatContain(exclusionGroups, u) {
                return exclusionGroups.filter((group) => group.includes(u));
            }

            function getNonPlacedUsers(exclusionGroups, positioned) {
                return exclusionGroups
                    .map((group) => group.filter((u) => !positioned.includes(u)))
                    .filter((g) => g.length);
            }

            function canBeIncluded(u, members, exclusionGroups) {
                if (members.length === 0) {
                    return true;
                }

                let groups = groupsThatContain(exclusionGroups, u);

                if (groups.length === 0) {
                    return true;
                }

                for (let i = 0; i < members.length; i++) {
                    let member = members[i];
                    if (groups.flat().includes(member)) {
                        return false;
                    }
                    // let containBoth = groupsThatContain(groups, member);
                    // if (containBoth.length > 0) {
                    //     return false;
                    // }
                }

                return true;
            }

            function getBiggestArray(arr) {
                let maxSize = Math.max(...arr.map((g) => g.length));
                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].length === maxSize) {
                        return arr[i];
                    }
                }

                return arr.reduce(function (a, b) {
                    return a.length > b.length ? a : b;
                }, []);
            }

            function onlyUnique(value, index, self) {
                return self.indexOf(value) === index;
            }

            let placed = [];

            let i = 0;

            if (userIds.length < numberOfGroups) {
                throw Error('more groups than users');
            }

            let toSet = exclusionGroups.flat().filter(onlyUnique);

            let groups = [];
            for (let j = 0; j < numberOfGroups; j++) {
                groups.push([]);
            }

            while (toSet.length > placed.length) {
                let nonPlacedUsers = getNonPlacedUsers(exclusionGroups, placed);
                let biggestArray = getBiggestArray(nonPlacedUsers);

                for (let k = 0; k < biggestArray.length; k++) {
                    let minSize = Math.min(...groups.map((g) => g.length));
                    let u = biggestArray[k];
                    for (let j = 0; j < groups.length; j++) {
                        let group = groups[j];
                        if (group.length !== minSize) {
                            continue;
                        }
                        if (canBeIncluded(u, group, exclusionGroups)) {
                            group.push(u);
                            placed.push(u);
                            break;
                        }
                    }
                }

                if (i > userIds.length) {
                    return undefined;
                }
                i++;
            }

            let remainingUsers = userIds.filter((u) => !placed.includes(u));

            if (remainingUsers.length) {
                for (let k = 0; k < remainingUsers.length; k++) {
                    let minSize = Math.min(...groups.map((g) => g.length));
                    let u = remainingUsers[k];
                    for (let j = 0; j < groups.length; j++) {
                        let group = groups[j];
                        if (group.length !== minSize) {
                            continue;
                        }
                        group.push(u);
                        placed.push(u);
                        break;
                    }
                }
            }

            return groups;
        },
    },
    props: {
        students: {
            type: Array,
            required: true,
        },
        absent: {
            type: Array,
            required: true,
        },
    },
    mounted() {
        this.selectPresent();

        const max = Math.floor(this.students.length / 2); //At most it would divide the class in pairs
        const array = [];
        for (let i = 2; i <= max; i++) {
            array.push(i);
        }
        return array;
    },
};
</script>
